import { formatDateString } from "@/domains/date/date";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { AppStore } from "@/store";
import { INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT } from "@/store/sync/operations/notes/CreateNoteOperation";
import { markdownModule } from "@mem-labs/common-editor";

interface NoteDetails {
  encodedContent: string;
  metadata: string[];
}

export const copyNotesAsMarkdown = async ({
  noteIds,
  store,
}: {
  noteIds: string[];
  store: AppStore;
}) => {
  trackEvent(TrackedEvent.NoteCopyAsMarkdown, { from: "notes_view" });
  try {
    const notes = (await store.notes.getManyAsync(noteIds)).filter(note => note);
    // Load note contents
    await Promise.all(
      notes.map(note =>
        note.fetchNoteContentDocument().catch(err => {
          logger.error({
            message: "Failed to fetch note content document",
            info: {
              error: objectModule.safeErrorAsJson(err as Error),
            },
          });
          return null;
        })
      )
    );
    // Convert to markdown
    const noteDetails = notes
      .map(note => {
        const encodedContent = note.noteContentDocument.data?.data?.model_data.encoded_content;
        if (!encodedContent || encodedContent === INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT) {
          return null;
        }

        const metadata: string[] = [];
        const ownerName = note.primaryOwner?.profileDisplayName;
        const ownerEmail = note.primaryOwner?.profileEmailAddress;
        const formattedCreatedAt = formatDateString(note.createdAt, "LLLL d, yyyy 'at' h:mm a");
        if (ownerName && ownerEmail) {
          metadata.push(`Created by: ${ownerName} <${ownerEmail}> at ${formattedCreatedAt}`);
        } else if (ownerName) {
          metadata.push(`Created by: ${ownerName} at ${formattedCreatedAt}`);
        } else if (ownerEmail) {
          metadata.push(`Created by: <${ownerEmail}> at ${formattedCreatedAt}`);
        }
        if (note.modifiedAt) {
          const formattedDate = formatDateString(note.modifiedAt, "LLLL d, yyyy 'at' h:mm a");
          metadata.push(`Last edited at: ${formattedDate}`);
        }
        const collectionNames = note.collectionList?.allCollections.map(
          collection => collection.label
        );
        if (collectionNames?.length > 0) {
          metadata.push(`Collections: ${collectionNames.join(", ")}`);
        }
        const url = windowModule.buildUrl({
          path: `/notes/${note.id}`,
        });
        metadata.push(`URL: ${url.toString()}`);

        return {
          encodedContent,
          metadata,
        };
      })
      .filter(Boolean) as NoteDetails[];
    const markdown = markdownModule.convertNoteEncodedContentAndMetadataToMarkdown({ noteDetails });
    // Write to clipboard
    await navigator.clipboard.writeText(markdown);
    toastModule.triggerToast({
      content: noteIds.length > 1 ? "Copied selected notes as Markdown" : "Copied as Markdown",
    });
  } catch (err) {
    logger.error({
      message: "Failed to copy notes as Markdown",
      info: {
        error: objectModule.safeErrorAsJson(err as Error),
      },
    });
  }
};
